import React, { useState, useEffect, useCallback } from 'react';
import apiService, { DailyPerformanceItem, DailyPerformanceResponse } from '../services/api';
import CashFlowIndicator from './CashFlowIndicator';

interface DailyPerformanceTableProps {
  isConsolidated?: boolean;
  userId?: string;
}

const DailyPerformanceTableBloomberg: React.FC<DailyPerformanceTableProps> = ({
  isConsolidated = false,
  userId
}) => {
  const [data, setData] = useState<DailyPerformanceItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 100,
    offset: 0,
    hasMore: true
  });
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const fetchData = useCallback(async (newOffset: number = 0, newLimit: number = 100) => {
    setLoading(true);
    try {
      let response: DailyPerformanceResponse;

      if (isConsolidated) {
        response = await apiService.getConsolidatedDailyPerformance(newLimit, newOffset);
      } else if (userId) {
        response = await apiService.getAdminUserDailyPerformance(userId, newLimit, newOffset);
      } else {
        response = await apiService.getDailyPerformance(newLimit, newOffset);
      }

      if (newOffset === 0) {
        setData(response.data || []);
      } else {
        setData(prev => [...prev, ...(response.data || [])]);
      }
      setPagination(response.pagination);
    } catch (error) {
      console.error('Error fetching daily performance:', error);
    } finally {
      setLoading(false);
    }
  }, [isConsolidated, userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const loadMore = () => {
    fetchData(pagination.offset + pagination.limit, pagination.limit);
  };

  const loadAll = () => {
    fetchData(0, 500);
  };

  const toggleRowExpansion = (date: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(date)) {
        newSet.delete(date);
      } else {
        newSet.add(date);
      }
      return newSet;
    });
  };

  const formatCurrency = (value: number): string => {
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Math.abs(value));
    return `$${formatted}`;
  };

  const formatPercent = (value: number): string => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const formatNumber = (value: number, decimals: number = 2): string => {
    return value.toFixed(decimals);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const month = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    return `${month} ${day} ${year}`;
  };

  // Determine cash flow type from the data
  const getCashFlowType = (item: DailyPerformanceItem): 'deposit' | 'withdrawal' | null => {
    if (!item.has_cash_flow) return null;
    if (!item.cash_flow_amount) return null;
    return item.cash_flow_amount > 0 ? 'deposit' : 'withdrawal';
  };

  // Calculate trading change (excluding cash flow)
  const getTradingChange = (item: DailyPerformanceItem): { amount: number; percent: number } => {
    if (!item.has_cash_flow) {
      return {
        amount: item.daily_change_usd,
        percent: item.daily_change_percent
      };
    }

    // If there's a cash flow, the trading change is the daily change minus the cash flow
    const tradingAmount = item.daily_change_usd - (item.cash_flow_amount || 0);
    // Calculate trading percent based on previous value minus cash flow
    const prevValue = item.total_value_usd - item.daily_change_usd;
    const tradingPercent = prevValue > 0 ? (tradingAmount / prevValue) * 100 : 0;

    return {
      amount: tradingAmount,
      percent: tradingPercent
    };
  };

  if ((!data || data.length === 0) && !loading) {
    return (
      <div className="bb-panel">
        <div className="bb-panel-header">
          DAILY PERFORMANCE HISTORY
        </div>
        <div className="bb-panel-content">
          <p style={{ color: 'var(--bb-text-secondary)' }}>NO PERFORMANCE DATA AVAILABLE</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bb-panel bloomberg-terminal">
      <div className="bb-header">
        <div className="bb-header-title">
          GRID LIQUIDITY BOT | DAILY PERFORMANCE
        </div>
        <div className="bb-header-info">
          <span>SHOWING {Math.min(data.length, pagination.total)} OF {pagination.total}</span>
          <span>{new Date().toISOString().split('T')[0]} {new Date().toTimeString().split(' ')[0]}</span>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="bb-table" style={{ tableLayout: 'fixed', width: '100%' }}>
          <thead>
            <tr className="bb-table-header">
              <th className="bb-table-cell" style={{ width: '10%', textAlign: 'left' }}>DATE</th>
              <th className="bb-table-cell" style={{ width: '15%', textAlign: 'right' }}>VALUE</th>
              <th className="bb-table-cell" style={{ width: '25%', textAlign: 'right' }}>CHG TYPE</th>
              <th className="bb-table-cell" style={{ width: '12%', textAlign: 'right' }}>SOL</th>
              <th className="bb-table-cell" style={{ width: '12%', textAlign: 'right' }}>USDC</th>
              <th className="bb-table-cell" style={{ width: '13%', textAlign: 'right' }}>DRIFT</th>
              <th className="bb-table-cell" style={{ width: '13%', textAlign: 'right' }}>ORCA</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => {
              const isExpanded = expandedRows.has(row.date);
              const cashFlowType = getCashFlowType(row);
              // For the first row (last in time), trading change should be zero
              const tradingData = index === data.length - 1
                ? { amount: 0, percent: 0 }
                : getTradingChange(row);

              return (
                <React.Fragment key={row.date}>
                  <tr className="bb-table-row">
                    <td className="bb-table-cell" style={{ textAlign: 'left' }}>
                      <span style={{ color: 'var(--bb-text-data)' }}>{formatDate(row.date)}</span>
                    </td>
                    <td className="bb-table-cell" style={{ textAlign: 'right' }}>
                      <span className="bb-value-neutral mono-number">{formatCurrency(row.total_value_usd)}</span>
                    </td>
                    <td className="bb-table-cell" style={{ textAlign: 'right' }}>
                      <CashFlowIndicator
                        type={cashFlowType}
                        amount={Math.abs(row.cash_flow_amount || 0)}
                        confidence={85} // You might want to add this to the API response
                        verified={true}
                        tradingChange={tradingData.amount}
                        tradingPercent={tradingData.percent}
                      />
                    </td>
                    <td className="bb-table-cell" style={{ textAlign: 'right' }}>
                      <span className="mono-number" style={{ color: 'var(--bb-text-data)' }}>
                        {formatNumber(row.wallet_balance_sol, 4)}
                      </span>
                    </td>
                    <td className="bb-table-cell" style={{ textAlign: 'right' }}>
                      <span className="mono-number" style={{ color: 'var(--bb-text-data)' }}>
                        {formatCurrency(row.wallet_balance_usdc)}
                      </span>
                    </td>
                    <td className="bb-table-cell" style={{ textAlign: 'right' }}>
                      <div>
                        <div className="mono-number" style={{ color: 'var(--bb-text-data)' }}>
                          {formatCurrency(row.drift.total_value)}
                        </div>
                        {row.drift.daily_change !== 0 && (
                          <div className={`text-xs ${row.drift.daily_change >= 0 ? 'bb-value-positive' : 'bb-value-negative'}`}>
                            {row.drift.daily_change >= 0 ? '+' : ''}{formatCurrency(row.drift.daily_change)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="bb-table-cell" style={{ textAlign: 'right' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                          <div className="mono-number" style={{ color: 'var(--bb-text-data)' }}>
                            {formatCurrency(row.orca.total_value)}
                          </div>
                          {row.orca.daily_change !== 0 && (
                            <div className={`text-xs ${row.orca.daily_change >= 0 ? 'bb-value-positive' : 'bb-value-negative'}`}>
                              {row.orca.daily_change >= 0 ? '+' : ''}{formatCurrency(row.orca.daily_change)}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => toggleRowExpansion(row.date)}
                          className="bb-button"
                          style={{
                            padding: '2px 6px',
                            fontSize: '10px',
                            marginLeft: '10px',
                            minWidth: '20px'
                          }}
                        >
                          {isExpanded ? '▲' : '▼'}
                        </button>
                      </div>
                    </td>
                  </tr>

                  {isExpanded && (
                    <tr className="bb-table-row" style={{ backgroundColor: 'var(--bb-bg-secondary)' }}>
                      <td colSpan={7} className="bb-table-cell">
                        <div className="bb-data-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                          {/* Drift Protocol Details */}
                          <div className="bb-data-cell">
                            <div>
                              <div className="bb-data-label">DRIFT PROTOCOL</div>
                              <div style={{ marginTop: '8px', fontSize: '11px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                  <span style={{ color: 'var(--bb-text-secondary)' }}>FREE COLLATERAL:</span>
                                  <span className="mono-number" style={{ color: 'var(--bb-text-data)' }}>
                                    {formatCurrency(row.drift.free_collateral)}
                                  </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                  <span style={{ color: 'var(--bb-text-secondary)' }}>UNREALIZED PNL:</span>
                                  <span className={`mono-number ${row.drift.unrealized_pnl >= 0 ? 'bb-value-positive' : 'bb-value-negative'}`}>
                                    {formatCurrency(row.drift.unrealized_pnl)}
                                  </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--bb-border)', paddingTop: '4px' }}>
                                  <span style={{ color: 'var(--bb-text-secondary)' }}>TOTAL:</span>
                                  <span className="mono-number" style={{ color: 'var(--bb-amber)', fontWeight: 'bold' }}>
                                    {formatCurrency(row.drift.total_value)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Orca Whirlpool Details */}
                          <div className="bb-data-cell">
                            <div>
                              <div className="bb-data-label">ORCA WHIRLPOOL</div>
                              <div style={{ marginTop: '8px', fontSize: '11px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                  <span style={{ color: 'var(--bb-text-secondary)' }}>POSITIONS VALUE:</span>
                                  <span className="mono-number" style={{ color: 'var(--bb-text-data)' }}>
                                    {formatCurrency(row.orca.positions_value)}
                                  </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                  <span style={{ color: 'var(--bb-text-secondary)' }}>UNCOLLECTED FEES:</span>
                                  <span className="mono-number bb-value-positive">
                                    {formatCurrency(row.orca.uncollected_fees)}
                                  </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--bb-border)', paddingTop: '4px' }}>
                                  <span style={{ color: 'var(--bb-text-secondary)' }}>TOTAL:</span>
                                  <span className="mono-number" style={{ color: 'var(--bb-amber)', fontWeight: 'bold' }}>
                                    {formatCurrency(row.orca.total_value)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Market Info */}
                          <div className="bb-data-cell">
                            <div>
                              <div className="bb-data-label">MARKET INFO</div>
                              <div style={{ marginTop: '8px', fontSize: '11px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                  <span style={{ color: 'var(--bb-text-secondary)' }}>SOL PRICE:</span>
                                  <span className="mono-number" style={{ color: 'var(--bb-text-data)' }}>
                                    {formatCurrency(row.sol_price)}
                                  </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                  <span style={{ color: 'var(--bb-text-secondary)' }}>WALLET SOL:</span>
                                  <span className="mono-number" style={{ color: 'var(--bb-text-data)' }}>
                                    {formatNumber(row.wallet_balance_sol, 4)}
                                  </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span style={{ color: 'var(--bb-text-secondary)' }}>WALLET USDC:</span>
                                  <span className="mono-number" style={{ color: 'var(--bb-text-data)' }}>
                                    {formatCurrency(row.wallet_balance_usdc)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div style={{
        padding: '12px 20px',
        borderTop: '1px solid var(--bb-border)',
        display: 'flex',
        justifyContent: 'center',
        gap: '12px'
      }}>
        {pagination.hasMore && (
          <>
            <button
              onClick={loadMore}
              disabled={loading}
              className="bb-button"
            >
              {loading ? 'LOADING...' : 'LOAD MORE [100 DAYS]'}
            </button>
            {data.length < 500 && (
              <button
                onClick={loadAll}
                disabled={loading}
                className="bb-button"
              >
                SHOW ALL [UP TO 500 DAYS]
              </button>
            )}
          </>
        )}
        {!pagination.hasMore && (
          <span style={{ color: 'var(--bb-text-secondary)', fontSize: '11px' }}>
            ALL DATA LOADED
          </span>
        )}
      </div>
    </div>
  );
};

export default DailyPerformanceTableBloomberg;